// app.functions — inspect a project's server functions (change B): the live
// registry (name → Function URL, published to `.s3p/functions/registry.json` by deploy)
// and each function's CloudWatch execution logs. Read-only management surface
// for the console Functions panel. IAM-gated: reading logs needs
// logs:FilterLogEvents on /aws/lambda/s3p-{slug}-fn-* (granted to owner/admin).

import {
  CloudWatchLogsClient,
  FilterLogEventsCommand,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-cloudwatch-logs@3.1054.0/+esm";
import {
  GetObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";
import { paths } from "./paths.ts";

export interface FunctionLogEvent {
  timestamp?: number;
  message?: string;
}

/** One entry of the PUBLIC deployed-functions manifest
 *  (`public/__s3p/functions.json`, written by `s3p build`). */
export interface DeployedFunctionInfo {
  name: string;
  description?: string;
  /** built-in (SDK-bundled) | platform (s3p control plane) | app (the
   *  project's own handler). */
  kind?: "app" | "platform" | "built-in";
  timeoutMs?: number;
  /** The `http:` path it declares, if any (served under `/api/` on the host). */
  http?: string;
}

export interface FunctionsModule {
  /** name → Function URL, from `.s3p/functions/registry.json` (published by `s3p deploy`). */
  registry(): Promise<Record<string, string>>;
  /** The Function URL for one function, or null if not deployed. */
  url(name: string): Promise<string | null>;
  /** The PUBLIC deployed-functions manifest — what's deployed, by name/kind.
   *  Read from THIS project's bucket, so a console embedded on another origin
   *  still lists the right project's functions (an origin-relative fetch of
   *  /__s3p/functions.json would answer for the hosting origin instead). */
  deployed(): Promise<DeployedFunctionInfo[]>;
  /** Recent CloudWatch execution log events for `s3p-{slug}-fn-{name}`,
   *  newest-last. Returns [] if the log group doesn't exist yet. */
  logs(name: string, opts?: { limit?: number; sinceMs?: number }): Promise<FunctionLogEvent[]>;
}

export interface FunctionsConfig {
  region: string;
  slug: string;
  bucket: string;
  getS3Client: () => Promise<S3Client>;
  getCredentials: () => Promise<{
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  }>;
}

export function createFunctionsAdmin(cfg: FunctionsConfig): FunctionsModule {
  async function registry(): Promise<Record<string, string>> {
    const s3 = await cfg.getS3Client().catch(() => null);
    if (!s3) return {};
    {
      try {
        const r = await s3.send(
          new GetObjectCommand({ Bucket: cfg.bucket, Key: paths().functionsRegistry() }),
        );
        const t = await (
          r.Body as { transformToString: () => Promise<string> }
        ).transformToString();
        return JSON.parse(t || "{}") as Record<string, string>;
      } catch {
        /* no registry published yet */
      }
    }
    return {};
  }

  return {
    registry,
    async url(name) {
      return (await registry())[name] ?? null;
    },
    async deployed() {
      const s3 = await cfg.getS3Client().catch(() => null);
      if (!s3) return [];
      try {
        const r = await s3.send(
          new GetObjectCommand({ Bucket: cfg.bucket, Key: paths().functionsManifest() }),
        );
        const t = await (
          r.Body as { transformToString: () => Promise<string> }
        ).transformToString();
        const data = JSON.parse(t || "[]") as DeployedFunctionInfo[];
        return Array.isArray(data) ? data : [];
      } catch {
        return []; /* nothing deployed yet */
      }
    },
    async logs(name, opts) {
      const c = await cfg.getCredentials();
      const cw = new CloudWatchLogsClient({
        region: cfg.region,
        credentials: {
          accessKeyId: c.accessKeyId,
          secretAccessKey: c.secretAccessKey,
          sessionToken: c.sessionToken,
        },
      });
      try {
        const r = await cw.send(
          new FilterLogEventsCommand({
            logGroupName: `/aws/lambda/s3p-${cfg.slug}-fn-${name}`,
            limit: opts?.limit ?? 100,
            startTime: opts?.sinceMs,
          }),
        );
        return (r.events ?? [])
          .map((e) => ({ timestamp: e.timestamp, message: e.message }))
          .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
      } catch {
        return [];
      }
    },
  };
}
